import styled from "@emotion/styled";
import { Box, IconButton } from "@mui/material";
import { useScroll } from "contexts/ScrollingContext";
import useGlobalStickyOnView from "hooks/charge/useGlobalSticky";
import { JSXElementConstructor, ReactElement, useEffect, useRef } from "react";
import { trackEventByNameAndCategory } from "reusable/ChargeGTM";
import { Mobile, NewText } from "ui";

const StyledFixed = styled(Box, {
    shouldForwardProp: (prop) =>
        !["noSectionsToShow", "adjustPositionInContainer"].includes(prop),
}) <{
    noSectionsToShow: boolean,
    adjustPositionInContainer: boolean
}>`
     ${(props) => props.theme.breakpoints.down("md")} {
       display: ${(props) => props.noSectionsToShow ? "none" : "flex"};
       z-index: 10;
      justify-content: space-around;
      position: relative;
      flex-direction: row;
      align-items: center;
      background: rgb(204, 220, 244);
      gap: 40px;
      border-bottom: 1px solid #000000;
      opacity: 0px;
      overflow-x: auto;
      font-size: 20px;
      font-weight: 500;
      line-height: 18px;
      height: 60px;
      width: 100%;
      padding-right: 20px;
      padding-left: 20px;
      &::-webkit-scrollbar {
        display: none;
        }
        width: 100%;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    ${(props) => props.adjustPositionInContainer && "width: 100vw; margin-left: -20px;"}
  }
`

const StyledIconButton = styled(IconButton, {
    shouldForwardProp: (prop) =>
        !["isSectionSelected"].includes(prop),
}) <{ isSectionSelected: boolean; }>`
  padding: 0px;
  text-align: start;
  :hover {
    background-color: none;
  }
  border-radius: 0px;
      ${(props) => props.theme.breakpoints.down("md")} {
    height: 100%;
    border-bottom: ${(props) => (props.isSectionSelected ? `4px solid ${props.theme.colors.yellow}` : 'none')};
  }
`

type Props = {
    adjustPositionInContainer?: boolean
}
function ScrollProviderMobile({ adjustPositionInContainer = false }: Props) {

    const ref = useRef<HTMLDivElement>(null)

    const { activeSection, scrollToSection, mobileFooterButonRefs, children } = useScroll()
    // Custom Hook That Mimic Sticky Behavior
    useGlobalStickyOnView({ targetRef: ref, offset: 64 })

    const handleClick = (child: ReactElement<any, string | JSXElementConstructor<any>>) => {
        scrollToSection(child.props.id)
        trackEventByNameAndCategory('mobile_navigation_home_page', child.props.id)
    }
    useEffect(() => {
        if (activeSection) {
            const element = mobileFooterButonRefs[activeSection]?.current
            if (element) {
                ref.current?.scrollTo({
                    left: element.offsetLeft - ref.current.clientWidth / 2 + element.clientWidth / 2,
                    behavior: "smooth",
                });
            }
        }
    }, [activeSection])
    return (
        <Mobile>
            <StyledFixed ref={ref} noSectionsToShow={children.length === 0} adjustPositionInContainer={adjustPositionInContainer} id="mobileFooter">
                {children?.map((child, index) => (
                    <StyledIconButton
                        style={{ backgroundColor: "transparent", fontFamily: "inherit" }}
                        key={index}
                        onClick={() => handleClick(child)}
                        isSectionSelected={activeSection === child.props.id}
                        ref={mobileFooterButonRefs[child.props.id]}>
                        <NewText
                            desktopFontSize={activeSection === child.props.id ? 18 : 16}
                            largeDesktopFontSize={activeSection === child.props.id ? 20 : 16}
                            largeDesktopLineHeight={
                                activeSection === child.props.id ? "28px" : "22.4px"
                            }
                            mobileFontSize={16}
                            component={"p"}
                            desktopFontWeight={
                                activeSection === child.props.id ? "600" : "400"
                            }
                            desktopLineHeight={
                                activeSection === child.props.id ? "19.6px" : "16.8px"
                            }
                        >
                            {child.props.id}
                        </NewText>
                    </StyledIconButton>
                ))}
            </StyledFixed>
        </Mobile>
    );
}

export default ScrollProviderMobile;
